import { isEmailValid } from "src/utils/methods";

export interface InviteOption {
  value?:
    | string
    | {
        id?: string | number;
        email?: string;
        name?: string;
      };
}

const TOKEN_SEPARATOR_REGEX = /[\n,;]+/;
const TRAILING_DELIMITER_REGEX = /[\s,;\n]+$/;
const ENDS_WITH_DELIMITER_REGEX = /[\s,;\n]$/;

export const getInviteTokens = (inputValue = ""): string[] => {
  const separatedByComma = inputValue
    .split(TOKEN_SEPARATOR_REGEX)
    .map((value) => value.trim())
    .filter(Boolean);

  return separatedByComma.flatMap((value) => {
    const parts = value.split(/\s+/).filter(Boolean);
    const shouldSplitBySpace =
      parts.length > 1 && parts.every((part) => part.includes("@"));

    return shouldSplitBySpace ? parts : [value];
  });
};

export const getInviteOptionKey = (
  option: InviteOption | null | undefined,
): string | null => {
  const value = option?.value;

  if (value && typeof value === "object" && value.id) {
    return `id:${value.id}`;
  }

  if (value && typeof value === "object" && value.email) {
    return `email:${value.email.toLowerCase()}`;
  }

  if (typeof value === "string") {
    return `email:${value.toLowerCase()}`;
  }

  return null;
};

export const findExistingInviteOption = <T extends InviteOption>(
  options: T[],
  token: string,
): T | undefined =>
  options.find((option) => {
    const optionValue = option?.value;

    if (!optionValue || typeof optionValue === "string") {
      return false;
    }

    return [optionValue.email, optionValue.name]
      .filter(Boolean)
      .some((value) => (value as string).toLowerCase() === token.toLowerCase());
  });

/**
 * Decides whether the current invite input should be turned into chip(s).
 *
 * Auto-creates on paste of 2+ complete emails in one go, and also as soon as a
 * delimiter (space/comma/semicolon/newline) is typed right after a single
 * completed email. The latter matters because isEmailValid doesn't require a
 * TLD, so without this a still-being-typed second address (e.g. "a@b") can
 * already look "valid" and get swept into an auto-create together with the
 * first one before the user finishes typing it.
 */
export const resolveInviteAutoCreate = (
  inputValue: string,
): { valueToCreate: string | null; nextInputValue: string } => {
  const endsWithDelimiter = ENDS_WITH_DELIMITER_REGEX.test(inputValue);
  const valueToCheck = endsWithDelimiter
    ? inputValue.replace(TRAILING_DELIMITER_REGEX, "")
    : inputValue;
  const inviteTokens = getInviteTokens(valueToCheck);

  const shouldAutoCreate =
    inviteTokens.length > 0 &&
    inviteTokens.every(isEmailValid) &&
    (inviteTokens.length > 1 || endsWithDelimiter);

  if (shouldAutoCreate) {
    return { valueToCreate: valueToCheck, nextInputValue: "" };
  }

  return { valueToCreate: null, nextInputValue: inputValue };
};
